/**
 * Currency Selector component.
 * Handles the currency dropdown functionality with proper event management.
 * @extends HTMLElement
 * @customElement currency-selector
 */
class CurrencySelector extends HTMLElement {
  /**
   * Initialises the currency selector component.
   * Sets up event listeners and element references.
   * @constructor
   */
  constructor() {
    super();

    // Cache DOM elements for better performance.
    this.elements = {
      input: this.querySelector('input[name="language_code"], input[name="country_code"]'),
      button: this.querySelector('.js-disclosure-button, [class*="js-disclosure-button"]'),
      panel: this.querySelector('.js-disclosure-list')
    };

    // Initialise component state.
    this.isOpen = false;

    // Bind event handlers to prevent memory leaks.
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleScroll = this.handleScroll.bind(this);
    this.boundOpenSelector = this.openSelector.bind(this);
    this.boundCloseSelector = this.closeSelector.bind(this);
    this.boundOnContainerKeyUp = this.onContainerKeyUp.bind(this);
    this.boundOnItemClick = this.onItemClick.bind(this);

    // Early return if required elements are missing.
    if (!this.elements.button || !this.elements.panel) {
      console.warn('Currency selector: Required elements not found.');
      return;
    }

    console.log('Currency selector: Initializing component.');
    this.initializeEventListeners();
  }

  /**
   * Sets up all event listeners for the component.
   * @returns {void}
   */
  initializeEventListeners() {
    // Add button event listeners.
    if (this.elements.button) {
      this.elements.button.addEventListener('click', this.boundOpenSelector);
      this.elements.button.addEventListener('focusout', this.boundCloseSelector);
    } else {
      console.error('Currency selector: Button element not found.');
      return;
    }

    // Add container event listeners.
    this.addEventListener('keyup', this.boundOnContainerKeyUp);

    // Add link event listeners.
    const links = this.querySelectorAll('.js-disclosure-link');
    if (links.length > 0) {
      links.forEach((item) => {
        item.addEventListener('click', this.boundOnItemClick);
      });
    }
  }

  /**
   * Hides the dropdown panel and cleans up event listeners.
   * @returns {void}
   */
  hidePanel() {
    if (this.elements.button) {
      this.elements.button.setAttribute('aria-expanded', 'false');
    }
    if (this.elements.panel) {
      this.elements.panel.setAttribute('hidden', 'true');
    }
    this.isOpen = false;

    // Remove global event listeners.
    document.removeEventListener('click', this.boundHandleClickOutside);
    window.removeEventListener('scroll', this.boundHandleScroll);
  }

  /**
   * Shows the dropdown panel and adds global event listeners.
   * @returns {void}
   */
  showPanel() {
    if (this.elements.button) {
      this.elements.button.setAttribute('aria-expanded', 'true');
    }
    if (this.elements.panel) {
      this.elements.panel.removeAttribute('hidden');
    }
    this.isOpen = true;

    // Add global event listeners.
    document.addEventListener('click', this.boundHandleClickOutside);
    window.addEventListener('scroll', this.boundHandleScroll);
  }

  /**
   * Handles keyboard events for the component.
   * @param {KeyboardEvent} event - The keyboard event.
   * @returns {void}
   */
  onContainerKeyUp(event) {
    // Early return if not escape key.
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.hidePanel();
    if (this.elements.button && this.elements.button instanceof HTMLElement) {
      this.elements.button.focus();
    }
  }

  /**
   * Handles currency selection from dropdown.
   * @param {Event} event - The click event.
   * @returns {void}
   */
  onItemClick(event) {
    event.preventDefault();

    const target = event.currentTarget;

    // Check if this is a redirect currency (like USD to US store).
    if (target && target instanceof HTMLElement && target.hasAttribute('data-redirect')) {
      const href = target.getAttribute('href');
      if (href && href !== '#') {
        window.location.href = href;
        return;
      }
    }

    const form = this.querySelector('form');

    if (this.elements.input && this.elements.input instanceof HTMLInputElement && target instanceof HTMLElement) {
      this.elements.input.value = target.dataset.value || '';
    }

    // Submit form if available.
    if (form) {
      form.submit();
    }
  }

  /**
   * Toggles the dropdown visibility.
   * @returns {void}
   */
  openSelector() {
    console.log('Currency selector: Button clicked, isOpen:', this.isOpen);

    if (this.elements.button && this.elements.button instanceof HTMLElement) {
      this.elements.button.focus();
    }

    if (this.isOpen) {
      this.hidePanel();
    } else {
      this.showPanel();
    }
  }

  /**
   * Handles focus out events for the button.
   * @param {FocusEvent} event - The focus event.
   * @returns {void}
   */
  closeSelector(event) {
    const relatedTarget = event.relatedTarget;
    const shouldClose = relatedTarget && relatedTarget instanceof HTMLElement && relatedTarget.nodeName === 'BUTTON';
    if (relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }

  /**
   * Handles clicks outside the dropdown.
   * @param {MouseEvent} event - The click event.
   * @returns {void}
   */
  handleClickOutside(event) {
    // Check if click is outside the dropdown.
    const target = event.target;
    if (target && target instanceof Node && !this.contains(target)) {
      this.hidePanel();
    }
  }

  /**
   * Handles page scroll events.
   * @returns {void}
   */
  handleScroll() {
    // Close dropdown on scroll.
    this.hidePanel();
  }

  /**
   * Cleanup method to remove event listeners.
   * @returns {void}
   */
  disconnectedCallback() {
    // Remove all event listeners to prevent memory leaks.
    if (this.elements.button) {
      this.elements.button.removeEventListener('click', this.boundOpenSelector);
      this.elements.button.removeEventListener('focusout', this.boundCloseSelector);
    }

    this.removeEventListener('keyup', this.boundOnContainerKeyUp);

    this.querySelectorAll('.js-disclosure-link').forEach((item) => {
      item.removeEventListener('click', this.boundOnItemClick);
    });

    // Remove global event listeners.
    document.removeEventListener('click', this.boundHandleClickOutside);
    window.removeEventListener('scroll', this.boundHandleScroll);
  }
}

// Register the custom element only if it hasn't been registered already.
if (!customElements.get('currency-selector')) {
  customElements.define('currency-selector', CurrencySelector);
}
